import { useState, type ReactNode } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import {
	ApiStatusError,
	approveRunnerRegistrationRequest,
	getRunnerRegistrationRequest,
	type Runner,
	type RunnerRegistrationRequest,
} from "@/lib/api";
import { focusRingOff, focusRingOn, INPUT_STYLE } from "@/lib/form-styles";
import { formatRunnerUserCode, isCompleteRunnerUserCode } from "@/lib/runner";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";

interface RunnerLinkFormProps {
	onRegistered?: (runner: Runner) => void;
}

function errorMessage(err: unknown, fallback: string): string {
	return err instanceof Error ? err.message : fallback;
}

/**
 * デバイスに出た確認コードでランナーの登録要求を探し、デバイス名とアカウント名を確かめて登録する。
 * 「デバイスを追加」の手順 4 で使う。他人のデバイスを登録させる手口を防ぐため、コードはリンクで受け取らず入力させる。
 */
export function RunnerLinkForm({ onRegistered }: RunnerLinkFormProps) {
	const organizationName = useCurrentUserStore((s) => s.organizationName);
	const [code, setCode] = useState("");
	const [request, setRequest] = useState<RunnerRegistrationRequest | null>(null);
	const [registered, setRegistered] = useState<Runner | null>(null);
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// 確認の画面から戻ったら、続けて入力できるようにする。
	const [focusInput, setFocusInput] = useState(false);

	const lookUp = async () => {
		setPending(true);
		setError(null);
		try {
			setRequest(await getRunnerRegistrationRequest(code));
		} catch (err) {
			setError(errorMessage(err, "確認コードの確認に失敗しました。"));
			throw err;
		} finally {
			setPending(false);
		}
	};

	const register = async (target: RunnerRegistrationRequest) => {
		setPending(true);
		setError(null);
		let runner: Runner;
		try {
			runner = await approveRunnerRegistrationRequest(target.userCode);
		} catch (err) {
			// 確かめている間に期限が切れたら、新しいコードの入力に戻す。
			if (err instanceof ApiStatusError && err.status === 404) {
				setRequest(null);
				setFocusInput(true);
			}
			setError(errorMessage(err, "デバイスの登録に失敗しました。"));
			throw err;
		} finally {
			setPending(false);
		}
		setRegistered(runner);
		onRegistered?.(runner);
	};

	if (registered)
		return (
			<p
				data-testid="runner-link-registered"
				style={{ margin: 0, color: "var(--success)", fontSize: "var(--text-ui)" }}
			>
				「{registered.name}」を登録しました。
			</p>
		);

	if (request)
		return (
			<div
				data-testid="runner-link-confirm"
				style={{ display: "flex", flexDirection: "column", gap: 12 }}
			>
				<dl
					className="select-text"
					style={{
						display: "grid",
						gridTemplateColumns: "auto 1fr",
						columnGap: 16,
						rowGap: 6,
						margin: 0,
						padding: "12px 14px",
						borderRadius: "var(--radius-3)",
						background: "var(--surface-2)",
						boxShadow: "var(--shadow-ring)",
						fontSize: "var(--text-ui)",
					}}
				>
					<Row testId="runner-link-user-code" label="確認コード" mono>
						{request.userCode}
					</Row>
					<Row testId="runner-link-name" label="デバイス名">
						{request.name || "—"}
					</Row>
					<Row testId="runner-link-account" label="アカウント名">
						{request.accountName || "—"}
					</Row>
					<Row testId="runner-link-organization" label="登録先の組織">
						{organizationName || "—"}
					</Row>
				</dl>
				<p style={{ margin: 0, color: "var(--warning)", fontSize: "var(--text-caption)" }}>
					登録すると、このデバイスで組織のエージェントが動きます。心当たりのあるデバイスか確かめてください。
				</p>
				{error && <ErrorLine>{error}</ErrorLine>}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
					<ConfirmButton
						label="別のコードを入力"
						loading={false}
						disabled={pending}
						variant="secondary"
						size="sm"
						onClick={() => {
							setRequest(null);
							setCode("");
							setError(null);
							setFocusInput(true);
						}}
						data-testid="runner-link-back"
					/>
					<ConfirmButton
						label="登録"
						loading={pending}
						size="sm"
						onClick={() => {
							void register(request).catch((err: unknown) => {
								console.error("デバイスの登録に失敗しました", err);
							});
						}}
						data-testid="runner-link-register"
					/>
				</div>
			</div>
		);

	const complete = isCompleteRunnerUserCode(code);
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<form
				data-testid="runner-link-form"
				onSubmit={(event) => {
					event.preventDefault();
					if (!complete || pending) return;
					void lookUp().catch((err: unknown) => {
						console.error("確認コードの確認に失敗しました", err);
					});
				}}
				style={{ display: "flex", alignItems: "center", gap: 8 }}
			>
				<input
					data-testid="runner-link-code"
					value={code}
					onChange={(event) => setCode(formatRunnerUserCode(event.target.value))}
					readOnly={pending}
					placeholder="XXXX-XXXX"
					autoFocus={focusInput}
					autoComplete="off"
					autoCapitalize="characters"
					spellCheck={false}
					className="select-text font-mono"
					onFocus={(event) => focusRingOn(event.currentTarget)}
					onBlur={(event) => focusRingOff(event.currentTarget)}
					style={{
						...INPUT_STYLE,
						width: 140,
						height: 28,
						padding: "0 8px",
						fontFamily: "var(--font-mono)",
						letterSpacing: "var(--tracking-label)",
					}}
				/>
				<ConfirmButton
					type="submit"
					label="確認"
					loading={pending}
					disabled={!complete}
					size="sm"
					data-testid="runner-link-lookup"
				/>
			</form>
			{error && <ErrorLine>{error}</ErrorLine>}
		</div>
	);
}

function Row({
	testId,
	label,
	mono = false,
	children,
}: {
	testId: string;
	label: string;
	mono?: boolean;
	children: ReactNode;
}) {
	return (
		<>
			<dt style={{ color: "var(--fg-3)" }}>{label}</dt>
			<dd
				data-testid={testId}
				className={mono ? "font-mono" : undefined}
				style={{ margin: 0, color: "var(--fg-1)", wordBreak: "break-all" }}
			>
				{children}
			</dd>
		</>
	);
}

function ErrorLine({ children }: { children: ReactNode }) {
	return (
		<p
			data-testid="runner-link-error"
			style={{ margin: 0, color: "var(--danger)", fontSize: "var(--text-caption)" }}
		>
			{children}
		</p>
	);
}
